<template>
  <DropDownCalendar
    :calendarModel="viewmodel"
    :range="range"
    :selectionMode="selectionMode"
    :disablePastDays="disablePastDays"
    @input:value="selectValue"
    @selectStartRange="updateDateRange"
    @selectEndRange="updateDateRange"
    @updateMode="updateMode"
    @updateMonth="updateMonth"
    @updateYear="updateYear" />
</template>

<script setup lang="ts">
import DropDownCalendar from "frontcore/presentation/calendar/views/DropDownCalendar.vue"
import useCoreCalendar from 'frontcore/presentation/calendar/domain/useCoreCalendar'
import {
  CalendarModeOptions,
  DateRange,
  DisabledDateFunction,
  InputDateViewModel,
} from 'frontcore/presentation/calendar/domain/types'
import { Nullable } from 'frontcore/presentation/types'
import { toRefs, watch } from 'vue'


interface Props {
  disableFutureDays?: boolean
  disablePastDays?: boolean
  disabled?: boolean
  disabledDays?: Nullable<DisabledDateFunction | Date[]>
  range?: boolean
  selectionMode?: CalendarModeOptions
  value?: InputDateViewModel['selectedDate']
}
const props = withDefaults(defineProps<Props>(), {
  dataTest: 'core-date-picker',
  disableFutureDays: false,
  disablePastDays: false,
  disabledDays: () => [],
  disabled: false,
  label: '',
  nullable: true,
  range: false,
  selectionMode: CalendarModeOptions.SELECT_DAY,
  value: null,
})

const {
  disabled,
  disabledDays,
  disableFutureDays,
  disablePastDays,
  range,
  selectionMode,
  value,
} = toRefs(props)

const emit = defineEmits<(e: 'input', selectedValue: InputDateViewModel['selectedDate']) => void>()

const {
  setCalendarSelectedDate,
  updateInternalValue,
  updateMode,
  updateMonth,
  updateYear,
  viewmodel,
} = useCoreCalendar({
  disabledDays: disabledDays,
  disableFutureDays: disableFutureDays,
  disablePastDays: disablePastDays,
  range: range,
  selectionMode: selectionMode,
  value: value,
})

watch(
  () => props.value,
  (newValue, oldValue) => {
    if (newValue === oldValue) { return }
    updateInternalValue(newValue)
    setCalendarSelectedDate()
  }, { immediate: true }
)

const selectValue = (selected: Date) => {
  if (disabled.value || range.value) return
  updateInternalValue(selected)
  emit('input', selected)
}

const updateDateRange = (selected: DateRange) => {
  if (disabled.value || !range.value) { return }
  updateInternalValue(selected)
  if (selected.length === 2) {
    if (selected[0] && selected[1]) {
      emit('input', selected)
    }
  }
}
</script>
